import { CalendarClock } from 'lucide-react';
import type { FxRates, Lead } from '../../lib/leadsApi';
import { Avatar } from '../tasks/linear/Avatar';
import { LabelPill } from '../tasks/LabelPill';
import {
  leadSourceText,
  TEMPERATURE_DOT,
  TEMPERATURE_LABEL,
  formatLeadValue,
  formatShortDate,
  isOverdue,
} from './leadConstants';

interface LeadListProps {
  leads: Lead[];
  selected: Set<string>;
  onToggleSelect: (id: string, shift?: boolean) => void;
  onOpen: (id: string) => void;
  onLabelClick?: (label: { id: string }) => void;
  nextActionDueByLead?: Record<string, string | null>;
  // Currency picked on the page; values in another one are converted at BNR rate.
  displayCurrency?: string;
  rates?: FxRates | null;
}

/**
 * Tabular list view for leads (CLE-191) — the "list = table like tasks" toggle.
 * Shows the key card fields as columns with multi-select checkboxes feeding the
 * bulk-actions bar.
 */
export function LeadList({
  leads,
  selected,
  onToggleSelect,
  onOpen,
  onLabelClick,
  nextActionDueByLead = {},
  displayCurrency,
  rates,
}: LeadListProps) {
  const allSelected = leads.length > 0 && leads.every((l) => selected.has(l.id));

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-3 py-2">
              <input
                type="checkbox"
                aria-label="Selectează tot"
                checked={allSelected}
                onChange={() => {
                  const everyone = leads.every((l) => selected.has(l.id));
                  leads.forEach((l) => {
                    if (everyone === selected.has(l.id)) onToggleSelect(l.id);
                  });
                }}
              />
            </th>
            <th className="px-3 py-2">Lead</th>
            <th className="px-3 py-2">Etapă</th>
            <th className="px-3 py-2">Companie</th>
            <th className="px-3 py-2">Valoare</th>
            <th className="px-3 py-2">Temp</th>
            <th className="px-3 py-2">Sursă</th>
            <th className="px-3 py-2">Responsabil</th>
            <th className="px-3 py-2">Următoarea acțiune</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {leads.map((lead) => {
            const due = nextActionDueByLead[lead.id] ?? null;
            const overdue = isOverdue(due);
            return (
              <tr
                key={lead.id}
                onClick={() => onOpen(lead.id)}
                className="cursor-pointer hover:bg-gray-50"
              >
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    aria-label={`Selectează ${lead.title}`}
                    checked={selected.has(lead.id)}
                    onChange={(e) =>
                      onToggleSelect(lead.id, (e.nativeEvent as MouseEvent).shiftKey)
                    }
                  />
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-col">
                    <span className="font-mono text-[11px] text-gray-400">{lead.identifier}</span>
                    <span className="font-medium text-gray-900">{lead.title}</span>
                    {lead.labels.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {lead.labels.map((l) => (
                          <span
                            key={l.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              onLabelClick?.(l);
                            }}
                          >
                            <LabelPill label={l} />
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">
                  {lead.stage && (
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        aria-hidden
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: lead.stage.color }}
                      />
                      {lead.stage.name}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-gray-600">{lead.companyName ?? '—'}</td>
                <td
                  className="px-3 py-2 text-gray-700"
                  title={formatLeadValue(lead.value, lead.currency, displayCurrency, rates).title}
                >
                  {formatLeadValue(lead.value, lead.currency, displayCurrency, rates).text}
                </td>
                <td className="px-3 py-2">
                  <span
                    className="inline-flex items-center gap-1.5"
                    title={TEMPERATURE_LABEL[lead.temperature]}
                  >
                    <span
                      aria-hidden
                      className={`h-2 w-2 rounded-full ${TEMPERATURE_DOT[lead.temperature]}`}
                    />
                    {TEMPERATURE_LABEL[lead.temperature]}
                  </span>
                </td>
                <td className="px-3 py-2 text-gray-600">{leadSourceText(lead)}</td>
                <td className="px-3 py-2">
                  {lead.assignee ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Avatar name={lead.assignee.name} userId={lead.assignee.id} size={20} />
                      <span className="text-gray-700">{lead.assignee.name}</span>
                    </span>
                  ) : (
                    <span className="text-gray-400">Neatribuit</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {due ? (
                    <span
                      className={`inline-flex items-center gap-1 ${
                        overdue ? 'font-medium text-red-600' : 'text-gray-600'
                      }`}
                    >
                      <CalendarClock size={12} /> {formatShortDate(due)}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
